import { useEffect, useState } from "react";
import { getCustomisationValue, getGroupData } from "../../../EditablePreview/EditablePreview";
import ModuleNumericalInput from "../../components/ModuleNumericalInput";
import { changeNumericalFieldValue, changeStringFieldValue, changeValue_noType } from "../../ModuleList";
import { renderFirstCustomBlock, renderSecondCustomBlock } from "../../customFields/CustomFieldRenderer";
import ModuleTextInput from "../../components/ModuleTextInput";
import Toggle from "../../customFields/toggle/toggleSwitch/Toggle";
import RangeSlider from "../../customFields/rangeSlider/slider/RangeSlider";
import AudioField from "../../customFields/audio/AudioField";
import './AudioModule.scss';

const AudioModule = (props) => {
    const moduleConfig = props.moduleConfig;
    const customisationVars = props.customisationVars;

    const nameDataLink = moduleConfig['audio-name'] ?? moduleConfig?.fields?.[0]?.dataLink;
    const enabledDataLink = moduleConfig['audio-enabled'] ?? moduleConfig?.fields?.[1]?.dataLink;
    const audioFileDataLink = moduleConfig['audio-file'] ?? moduleConfig?.fields?.[2]?.dataLink;
    const loopDataLink = moduleConfig['audio-loop'] ?? moduleConfig?.fields?.[4]?.dataLink;
    const delayDataLink = moduleConfig['audio-delay'] ?? moduleConfig?.fields?.[5]?.dataLink;
    const volumeDataLink = moduleConfig['audio-volume'] ?? moduleConfig?.fields?.[3]?.dataLink;

    const audioFileGroupArray = audioFileDataLink ? getGroupData(customisationVars, audioFileDataLink, -1) : null;

    // Per-file enable/disable: derived from audio-file dataLink by swapping the last
    // segment (e.g. "filename") for "playSFX". Allows each file in a multi-file slot
    // to be individually disabled without touching the slot-level toggle.
    const perFileEnabledDataLink = audioFileDataLink
        ? audioFileDataLink.split('.').slice(0, -1).join('.') + '.playSFX'
        : null;

    const [name, setName] = useState(getCustomisationValue(customisationVars, nameDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null);

    // Slot-level enabled — used only for visual dimming of the whole panel, not for Behaviour buttons.
    const enabledGroupArray = enabledDataLink ? getGroupData(customisationVars, enabledDataLink, -1, true) : null;
    let enabledIndex = props.index ?? 0;
    if (enabledGroupArray && audioFileGroupArray && enabledGroupArray !== audioFileGroupArray && enabledGroupArray.length >= props.index) {
        enabledIndex = enabledGroupArray.length - 1;
    }
    const [enabled, setEnabled] = useState(getCustomisationValue(customisationVars, enabledDataLink, enabledIndex, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null);

    // Per-file enabled state (playSFX). undefined → treat as true (enabled).
    const [perFileEnabled, setPerFileEnabled] = useState(
        perFileEnabledDataLink
            ? (getCustomisationValue(customisationVars, perFileEnabledDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? true)
            : null
    );

    const [audioFile, setAudioFile] = useState(getCustomisationValue(customisationVars, audioFileDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null);
    const [loop, setLoop] = useState(getCustomisationValue(customisationVars, loopDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null);
    const [delay, setDelay] = useState(getCustomisationValue(customisationVars, delayDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null);
    const [volume, setVolume] = useState(getCustomisationValue(customisationVars, volumeDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null);

    const showTypeSelector = !!perFileEnabledDataLink || !!enabledDataLink || !!loopDataLink;

    useEffect(() => {
        if (nameDataLink) {
            const nameValue = getCustomisationValue(customisationVars, nameDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setName(nameValue);
        }
    }, [nameDataLink, customisationVars, props.index, props.visualisationComponentKey, props.selectedFeatureNode, props.forceUpdate]);
    useEffect(() => {
        if (enabledDataLink) {
            const enabledValue = getCustomisationValue(customisationVars, enabledDataLink, enabledIndex, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setEnabled(enabledValue);
        }
    }, [enabledDataLink, customisationVars, enabledIndex, props.index, props.visualisationComponentKey, props.selectedFeatureNode, props.forceUpdate]);
    useEffect(() => {
        if (perFileEnabledDataLink) {
            const val = getCustomisationValue(customisationVars, perFileEnabledDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setPerFileEnabled(val ?? true);
        }
    }, [perFileEnabledDataLink, customisationVars, props.index, props.visualisationComponentKey, props.selectedFeatureNode, props.forceUpdate]);
    useEffect(() => {
        if (audioFileDataLink) {
            const audioFileValue = getCustomisationValue(customisationVars, audioFileDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setAudioFile(audioFileValue);
        }
    }, [audioFileDataLink, customisationVars, props.index, props.visualisationComponentKey, props.selectedFeatureNode, props.forceUpdate]);
    useEffect(() => {
        if (loopDataLink) {
            const loopValue = getCustomisationValue(customisationVars, loopDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setLoop(loopValue);
        }
    }, [loopDataLink, customisationVars, props.index, props.visualisationComponentKey, props.selectedFeatureNode, props.forceUpdate]);
    useEffect(() => {
        if (delayDataLink) {
            const delayValue = getCustomisationValue(customisationVars, delayDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setDelay(delayValue);
        }
    }, [delayDataLink, customisationVars, props.index, props.visualisationComponentKey, props.selectedFeatureNode, props.forceUpdate]);
    useEffect(() => {
        if (volumeDataLink) {
            const volumeValue = getCustomisationValue(customisationVars, volumeDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setVolume(volumeValue);
        }
    }, [volumeDataLink, customisationVars, props.index, props.visualisationComponentKey, props.selectedFeatureNode, props.forceUpdate]);

    const changeVolumeFieldValue = (value) => {
        if (volumeDataLink) {
            if (changeNumericalFieldValue(customisationVars, { dataLink: volumeDataLink }, value, true, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setVolume(value);
            }
        }
    };
    const changeDelayFieldValue = (value) => {
        if (delayDataLink) {
            if (changeNumericalFieldValue(customisationVars, { dataLink: delayDataLink }, value, true, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setDelay(value);
            }
        }
    };
    const changeLoopFieldValue = (value) => {
        if (loopDataLink) {
            if (changeValue_noType(customisationVars, { dataLink: loopDataLink }, value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setLoop(value);
            }
        }
    };
    /* const changeAudioFileFieldValue = (value) => {
        if (audioFileDataLink) {
            if (changeStringFieldValue(customisationVars, { dataLink: audioFileDataLink }, value, false, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setAudioFile(value);
            }
        }
    }; */
    const changeNameFieldValue = (value) => {
        if (nameDataLink) {
            if (changeStringFieldValue(customisationVars, { dataLink: nameDataLink }, value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setName(value);
            }
        }
    };
    const changeEnabledFieldValue = (value) => {
        if (enabledDataLink) {
            if (changeValue_noType(customisationVars, { dataLink: enabledDataLink }, value, enabledIndex, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setEnabled(value);
                props.forceUpdateComponent();
                props.updateModulesListFunc();
            }
        }
    };

    // Per-file enable/disable — writes playSFX at props.index (individual file control).
    // Falls back to slot-level changeEnabledFieldValue if no per-file dataLink.
    const changePerFileEnabledFieldValue = (value) => {
        if (perFileEnabledDataLink) {
            if (changeValue_noType(customisationVars, { dataLink: perFileEnabledDataLink }, value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setPerFileEnabled(value);
                props.forceUpdateComponent();
                props.updateModulesListFunc();
            }
        } else {
            changeEnabledFieldValue(value);
        }
    };

    // Dim non-behaviour sections when the SLOT-LEVEL toggle is off.
    const isDisabledByToggle = enabledDataLink && enabled === false;
    // Per-file: a file is individually disabled when playSFX is explicitly false.
    const isThisFileDisabled = perFileEnabledDataLink ? perFileEnabled === false : (enabledDataLink && !enabled);
    const isThisFileActive   = !isThisFileDisabled;

    /* const clickHandler = () => {
        props.openTemporaryAudioUploadPopup(
            audioFile,
            'Timeline Audio > ' + (name || 'Unnamed Audio'),
            'This is a temporary audio uploader and is not final.',
        )
    } */

    return (
    <>
        <div className="v2-basic-module-holder" style={isThisFileDisabled ? { opacity: 0.45 } : undefined}>
            <h5>Audio</h5>

            <div className='v2-module-columns-holder wrap'>

                {nameDataLink &&
                    <div className='v2-module-one-column v2-full-width'>
                        <div className={`v2-module-field-label${!!nameDataLink ? '' : ' disabled'}`}>Name</div>
                        <ModuleTextInput
                            value={getCustomisationValue(customisationVars, nameDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null}
                            fieldId={'audio-name-field'}
                            enabled={!!nameDataLink}
                            editable={true}
                            onChange={(fieldId, value) => {changeNameFieldValue(value);}}
                        />
                    </div>
                }

                <div className='v2-module-one-column v2-full-width'>
                    <AudioField
                        fieldData={{ customFieldConfig: { dataLink: audioFileDataLink, customFieldLabel: 'Audio File' } }}
                        moduleConfig={moduleConfig}
                        groupConfig={props.groupConfig}
                        customisationVars={customisationVars}
                        index={props.index}
                        visualisationComponentKey={props.visualisationComponentKey}
                        selectedFeatureNode={props.selectedFeatureNode}
                        isEnabled={!!audioFileDataLink && isThisFileActive}
                        openTemporaryAudioUploadPopup={props.openTemporaryAudioUploadPopup}
                        value={getCustomisationValue(customisationVars, audioFileDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null}
                    />
                </div>
            </div>
        </div>

        {(perFileEnabledDataLink || enabledDataLink || loopDataLink || delayDataLink || volumeDataLink) &&
            <div className="v2-basic-module-holder hoist">
                    <h5>Playback</h5>

                <div className='v2-module-columns-holder wrap'>

                    <div className='v2-module-one-column'>
                        <div className={`v2-module-field-label${!showTypeSelector ? ' disabled' : ''}`}>Behaviour</div>
                        <div className={`v2-module-square-button-group${!showTypeSelector ? ' v2-module-disabled-field' : ''}`}>
                            <div className='v2-module-square-button-holder'>
                                <div className={`v2-module-square-button${isThisFileDisabled ? ' v2-module-button__active v2-no-mouse-events' : ''}${!perFileEnabledDataLink && !enabledDataLink ? ' v2-module-disabled-field' : ''}`}
                                    onClick={() => changePerFileEnabledFieldValue(false)}
                                >
                                    <div className={`v2-module-square-button__icon v2-module-audio-playback-disabled`} />
                                </div>
                            </div>
                            <div className='v2-module-square-button-holder'>
                                <div className={`v2-module-square-button${isThisFileActive && ((loopDataLink && !loop) || !loopDataLink) ? ' v2-module-button__active v2-no-mouse-events' : ''}${(!loopDataLink && !perFileEnabledDataLink && !enabledDataLink) ? ' v2-module-disabled-field' : ''}`}
                                    onClick={() => {changePerFileEnabledFieldValue(true); changeLoopFieldValue(false);}}
                                >
                                    <div className={`v2-module-square-button__icon v2-module-audio-playback-no-loop`} />
                                </div>
                            </div>
                            <div className='v2-module-square-button-holder'>
                                <div className={`v2-module-square-button${isThisFileActive && (loopDataLink && loop) ? ' v2-module-button__active v2-no-mouse-events' : ''}${!loopDataLink ? ' v2-module-disabled-field' : ''}`}
                                    onClick={() => {changePerFileEnabledFieldValue(true); changeLoopFieldValue(true);}}
                                >
                                    <div className={`v2-module-square-button__icon v2-module-audio-playback-loop`} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {delayDataLink &&
                        <div className='v2-module-one-column' style={isDisabledByToggle ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
                            <div className={`v2-module-field-label${!!delayDataLink ? '' : ' disabled'}`}>Delay (ms)</div>
                            <ModuleNumericalInput
                                value={delay}
                                fieldId={'audio-delay-field'}
                                enabled={!!delayDataLink}
                                onChange={(fieldId, value) => changeDelayFieldValue(value)}
                                hiddenDropDown={true}
                                hiddenDropDownOptions={[0, 100, 250, 500, 750, 1000, 1500, 2000]}
                                dropDownValue={delay}
                            />
                        </div>
                    }

                    {volumeDataLink &&
                        <div className='v2-module-one-column v2-full-width' style={isDisabledByToggle ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
                            <div className={`v2-module-field-label${!!volumeDataLink ? '' : ' disabled'}`}>Volume</div>
                            <div className={`v2-module-square-button-group no-gap${!volumeDataLink ? ' v2-module-disabled-field' : ''}`}>
                                <div className={`v2-module-square-button__icon v2-module-audio-volume-icon`} />
                                <div className={`v2-module-square-control-holder${!volumeDataLink ? ' v2-module-disabled-field' : ''}`}>
                                    <RangeSlider
                                        min={0}
                                        max={100}
                                        value={volume ?? 100}
                                        onChange={volumeDataLink ? (e) => changeVolumeFieldValue(e.target.value) : null}
                                    />
                                </div>
                                <div className="v2-volume-holder">{volume ?? 100}%</div>
                            </div>
                        </div>
                    }

                </div>
            </div>
        }
    </>
    );
};

export default AudioModule;
